import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { Spinner } from "react-bootstrap";
import { BsArrowLeft } from "react-icons/bs";
import { DateTime } from "luxon";
import ApiService from "../../ApiService";
import { SnackbarContext } from "../AlertSystem";
import "./PrepDashboard.css";
import { PREP_PARTNER_OPTIONS } from "../../constants/prepPartnerOptions";
import { TIMEZONE_OPTIONS, getInitialTimezone, formatDate } from "./prepDashboardUtils";

const PARTNER_OPTIONS = [
  { value: "", label: "All Partners" },
  ...PREP_PARTNER_OPTIONS.map((p) => ({ value: p.id, label: p.name })),
];

const PERIOD_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "CUSTOM", label: "Custom" },
];

const INTAKE_FUNNEL = [
  { key: null, label: "Total Jotform Submissions", countKey: "totalJotformSubmissions", pctKey: null, parentLabel: null, step: 1 },
  { key: "validatedPatient", label: "Validated Patients", countKey: "validatedPatientCount", pctKey: "validatedPatientPercentOfSubmissions", parentLabel: "of submissions", step: 2 },
  { key: "stiTestingCompleted", label: "Completed STI Testing", countKey: "completedStiTestingCount", pctKey: "completedStiTestingPercentOfValidated", parentLabel: "of validated", step: 3 },
  { key: "receivedPrepRx", label: "Received PrEP Rx", countKey: "receivedPrepRxCount", pctKey: "receivedPrepRxPercentOfValidated", parentLabel: "of validated", pct2Key: "receivedPrepRxPercentOfStiCompleted", pct2Label: "of STI completed", step: 4 },
];

const MAINTENANCE_FUNNEL = [
  { key: "maintenancePatient", label: "Maintenance Patients", countKey: "maintenancePatientCount", pctKey: "maintenancePercentOfValidated", parentLabel: "of validated", step: 5 },
  { key: "maintenanceQuestionnaireCompleted", label: "Questionnaire Completed", countKey: "maintenanceQuestionnaireCompletedCount", pctKey: "maintenanceQuestionnaireCompletedPercent", parentLabel: "of maintenance", step: 6 },
  { key: "maintenanceTestingCompleted", label: "Testing Completed", countKey: "maintenanceTestingCompletedCount", pctKey: "maintenanceTestingCompletedPercent", parentLabel: "of maintenance", step: 7 },
  { key: "maintenanceNextRxReceived", label: "Next Rx Received", countKey: "maintenanceNextRxReceivedCount", pctKey: "maintenanceNextRxReceivedPercent", parentLabel: "of maintenance", step: 8 },
  { key: "over75DaysFromRx", label: ">75 Days from Rx", countKey: "maintenanceOver75DaysFromRxCount", pctKey: "maintenanceOver75DaysFromRxPercent", parentLabel: "of maintenance", accent: "warning", step: 9 },
  { key: "lapsed", label: "Lapsed (>90 Days)", countKey: "lapsedPatientCount", pctKey: "lapsedPercentOfMaintenance", parentLabel: "of maintenance", pct2Key: "lapsedPercentOfTotalPatients", pct2Label: "of total patients", accent: "danger", step: 10 },
];

const ALL_FUNNEL_STEPS = [...INTAKE_FUNNEL, ...MAINTENANCE_FUNNEL];

function fmtNum(val) {
  if (val == null) return "\u2014";
  return typeof val === "number" ? val.toLocaleString() : val;
}

function fmtPct(val) {
  if (val == null) return null;
  return `${Number(val).toFixed(1)}%`;
}

function fmtDays(val) {
  if (val == null) return "\u2014";
  return `${Number(val).toFixed(1)}`;
}

function fmtBool(val) {
  if (val == null) return "\u2014";
  return val ? "Yes" : "No";
}

function getPatientStage(p) {
  if (p.lapsed) return { label: "Lapsed", color: "danger" };
  if (p.over75DaysFromRx) return { label: ">75d Rx", color: "warning" };
  if (p.maintenancePatient) return { label: "Maintenance", color: "info" };
  if (p.receivedPrepRx) return { label: "PrEP Rx", color: "success" };
  if (p.stiTestingCompleted) return { label: "STI Done", color: "success" };
  if (p.validatedPatient) return { label: "Validated", color: "neutral" };
  return { label: "Submitted", color: "muted" };
}

// ─── Active Filter Banner ─────────────────────────────────────────────────────

const FilterBanner = ({ partner, period, fromDate, toDate, timezone, summary, onClearPartner }) => {
  const partnerLabel = partner
    ? PARTNER_OPTIONS.find((o) => o.value === partner)?.label || partner
    : "All Partners";

  let dateLabel;
  if (summary?.fromDate && summary?.toDate) {
    const from = DateTime.fromISO(summary.fromDate);
    const to = DateTime.fromISO(summary.toDate);
    if (from.isValid && to.isValid) {
      dateLabel = `${from.toFormat("LLL dd, yyyy")} \u2013 ${to.toFormat("LLL dd, yyyy")}`;
    }
  }
  if (!dateLabel) {
    if (period === "CUSTOM" && fromDate && toDate) {
      const from = DateTime.fromISO(fromDate);
      const to = DateTime.fromISO(toDate);
      dateLabel = from.isValid && to.isValid
        ? `${from.toFormat("LLL dd, yyyy")} \u2013 ${to.toFormat("LLL dd, yyyy")}`
        : "Custom Range";
    } else if (period === "CUSTOM") {
      dateLabel = "Custom (select dates)";
    } else {
      dateLabel = PERIOD_OPTIONS.find((o) => o.value === period)?.label || period;
    }
  }

  const tzLabel = TIMEZONE_OPTIONS.find((o) => o.value === timezone)?.label || timezone;

  return (
    <div className="pd__filter-banner">
      <span className="pd__filter-banner-label">Showing:</span>
      {/* TODO: Re-enable partner pill when partner filter is restored
      <div className="pd__filter-pill">
        <span className="pd__filter-pill-label">Partner</span>
        {partnerLabel}
        {partner && (
          <button className="pd__filter-pill-clear" onClick={onClearPartner} aria-label="Clear partner filter">&times;</button>
        )}
      </div>
      */}
      <div className="pd__filter-pill pd__filter-pill--accent">
        <span className="pd__filter-pill-label">Range</span>
        {dateLabel}
      </div>
      <div className="pd__filter-pill">
        <span className="pd__filter-pill-label">TZ</span>
        {tzLabel}
      </div>
    </div>
  );
};

// ─── Funnel Step ──────────────────────────────────────────────────────────────

const FunnelStep = ({ step, data, loading, topCount, isActive, onClick }) => {
  const count = data?.[step.countKey] ?? 0;
  const pct = step.pctKey ? data?.[step.pctKey] : null;
  const pct2 = step.pct2Key ? data?.[step.pct2Key] : null;
  const barPct = topCount > 0 ? Math.min((count / topCount) * 100, 100) : 0;
  const isClickable = !!step.key;

  const cls = [
    "pd__funnel-step",
    step.accent && `pd__funnel-step--${step.accent}`,
    isClickable && "pd__funnel-step--clickable",
    isActive && "pd__funnel-step--active",
  ].filter(Boolean).join(" ");

  return (
    <div
      className={cls}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="pd__funnel-step-header">
        <span className="pd__funnel-step-label">
          <span className="pd__funnel-step-num">{step.step}</span>
          {step.label}
        </span>
        <span className="pd__funnel-step-count">
          {loading ? <Spinner animation="border" size="sm" /> : fmtNum(count)}
        </span>
      </div>
      {!loading && (
        <>
          <div className="pd__funnel-bar-track">
            <div className="pd__funnel-bar-fill" style={{ width: `${barPct}%` }} />
          </div>
          <div className="pd__funnel-step-meta">
            {pct != null && <span>{fmtPct(pct)} {step.parentLabel}</span>}
            {pct2 != null && <span className="pd__funnel-step-meta-sep">{fmtPct(pct2)} {step.pct2Label}</span>}
          </div>
        </>
      )}
    </div>
  );
};

// ─── KPI Sidebar ──────────────────────────────────────────────────────────────

const KpiPanel = ({ data, loading }) => {
  const d = data || {};
  return (
    <div className="pd__kpi-panel">
      <div className="pd__kpi-panel-title">Operational KPIs</div>
      <div className="pd__kpi-card">
        <div className="pd__kpi-label">Avg Days to STI Testing</div>
        <div className="pd__kpi-value">{loading ? <Spinner animation="border" size="sm" /> : fmtDays(d.avgDaysToCompleteStiTesting)}</div>
        <div className="pd__kpi-sub">order to result</div>
      </div>
      <div className="pd__kpi-card">
        <div className="pd__kpi-label">Avg Days IHD to Ship</div>
        <div className="pd__kpi-value">{loading ? <Spinner animation="border" size="sm" /> : fmtDays(d.avgDaysIhdToShip)}</div>
        {!loading && fmtPct(d.percentIhdShippedWithinSla) && (
          <div className="pd__kpi-sub">{fmtPct(d.percentIhdShippedWithinSla)} within SLA</div>
        )}
        {!loading && d.ihdOrdersShippedCount != null && (
          <div className="pd__kpi-sub">{fmtNum(d.ihdOrdersShippedCount)} orders shipped</div>
        )}
      </div>
      <div className="pd__kpi-card">
        <div className="pd__kpi-label">Avg Days Results After Testing</div>
        <div className="pd__kpi-value">{loading ? <Spinner animation="border" size="sm" /> : fmtDays(d.avgDaysResultsAfterTesting)}</div>
        <div className="pd__kpi-sub">
          {!loading && d.resultsTimingDenominator != null
            ? `based on ${fmtNum(d.resultsTimingDenominator)} results`
            : "lab received to result"}
        </div>
      </div>
    </div>
  );
};

// ─── Patient Detail Panel ───────────────────────────────────────────────────

const PatientDetail = ({ email, partnerId, onClose, timezone }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getPrepDashboardPatientByEmail({
        email,
        partnerId: partnerId || undefined,
        includeKitLevel: true,
      });
      const d = res.data;
      setRows(Array.isArray(d) ? d : d ? [d] : []);
    } catch (err) {
      setError(err?.response?.status === 404 ? "Patient not found." : "Failed to load patient details.");
    } finally {
      setLoading(false);
    }
  }, [email, partnerId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) {
    return (
      <div className="pd__detail">
        <div className="pd__loading"><Spinner animation="border" /><div style={{ marginTop: 12 }}>Loading patient details...</div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pd__detail">
        <div className="pd__error">{error}<br /><button className="pd__action-btn" onClick={onClose}>Back to list</button></div>
      </div>
    );
  }

  if (!rows.length) return null;

  const first = rows[0];

  return (
    <div className="pd__detail">
      <div className="pd__detail-header">
        <h3 className="pd__detail-title">
          {`${first.firstName || ""} ${first.lastName || ""}`.trim() || first.email || "Patient Detail"}
        </h3>
        <button className="pd__action-btn" onClick={onClose}>Close</button>
      </div>

      <div className="pd__detail-grid">
        <div className="pd__detail-field"><span className="pd__detail-label">Email</span><span className="pd__detail-value">{first.email || "\u2014"}</span></div>
        <div className="pd__detail-field"><span className="pd__detail-label">Patient ID</span><span className="pd__detail-value">{first.patientId || "\u2014"}</span></div>
        <div className="pd__detail-field"><span className="pd__detail-label">Partner</span><span className="pd__detail-value">{first.partnerId || "\u2014"}</span></div>
      </div>

      <div className="pd__detail-subtitle">Orders &amp; Kits</div>
      <div className="pd__detail-table-wrap">
        <table className="pd__table pd__table--compact">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Created</th>
              <th>Status</th>
              <th>Beluga</th>
              <th>Jotform ID</th>
              <th>Validated</th>
              <th>STI Done</th>
              <th>Days STI</th>
              <th>IHD Days</th>
              <th>SLA</th>
              <th>Lab Days</th>
              <th>PrEP Rx</th>
              <th>Maint.</th>
              <th>Quest.</th>
              <th>Maint. Test</th>
              <th>Days Since Rx</th>
              <th>&gt;75d Rx</th>
              <th>Next Rx</th>
              <th>Lapsed</th>
              <th>Kit ID</th>
              <th>Kit Received</th>
              <th>Result Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.orderId}-${r.kitId}-${i}`}>
                <td className="pd__cell-mono">{r.orderId || "\u2014"}</td>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(r.orderCreateDate, timezone)}</td>
                <td>{r.orderStatus || "\u2014"}</td>
                <td>{r.belugaVisitStatus || "\u2014"}</td>
                <td className="pd__cell-mono">{r.jotformSubmissionId || "\u2014"}</td>
                <td>{r.validatedPatient ? "Yes" : "No"}</td>
                <td>{r.stiTestingCompleted ? "Yes" : "No"}</td>
                <td>{r.daysToCompleteStiTesting != null ? fmtDays(r.daysToCompleteStiTesting) : "\u2014"}</td>
                <td>{r.daysIhdToShip != null ? fmtDays(r.daysIhdToShip) : "\u2014"}</td>
                <td>{fmtBool(r.ihdShippedWithinSla)}</td>
                <td>{r.daysResultsAfterTesting != null ? fmtDays(r.daysResultsAfterTesting) : "\u2014"}</td>
                <td>{r.receivedPrepRx ? "Yes" : "No"}</td>
                <td>{r.maintenancePatient ? "Yes" : "No"}</td>
                <td>{fmtBool(r.maintenanceQuestionnaireCompleted)}</td>
                <td>{fmtBool(r.maintenanceTestingCompleted)}</td>
                <td>{r.daysSinceLastRx != null ? Math.round(r.daysSinceLastRx) : "\u2014"}</td>
                <td>{r.over75DaysFromRx ? <span className="pd__badge pd__badge--warning">Yes</span> : fmtBool(r.over75DaysFromRx)}</td>
                <td>{fmtBool(r.maintenanceNextRxReceived)}</td>
                <td>{r.lapsed ? <span className="pd__badge pd__badge--danger">Yes</span> : fmtBool(r.lapsed)}</td>
                <td className="pd__cell-mono">{r.kitId || "\u2014"}</td>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(r.kitReceivedAtLabDate, timezone)}</td>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(r.testResultDate, timezone)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Kit Lookup ─────────────────────────────────────────────────────────────

const KitLookup = ({ timezone }) => {
  const { showSnackbar } = useContext(SnackbarContext);
  const [kitIdInput, setKitIdInput] = useState("");
  const [kitData, setKitData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    const id = kitIdInput.trim();
    if (!id) return;
    setLoading(true);
    setKitData(null);
    try {
      const res = await ApiService.getPrepDashboardKit(id);
      setKitData(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        showSnackbar({ message: `Kit "${id}" not found.`, severity: "warning" });
      } else {
        showSnackbar({ message: "Failed to look up kit.", severity: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const d = kitData;

  return (
    <div className="pd__kit-lookup">
      <div className="pd__kit-lookup-bar">
        <span className="pd__kit-lookup-label">Kit Lookup</span>
        <input
          type="text"
          className="pd__filter-input"
          placeholder="Enter Kit ID..."
          value={kitIdInput}
          onChange={(e) => setKitIdInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button className="pd__action-btn pd__action-btn--primary" onClick={handleSearch} disabled={loading || !kitIdInput.trim()}>
          {loading ? <Spinner animation="border" size="sm" /> : "Search"}
        </button>
      </div>

      {d && (
        <div className="pd__detail" style={{ marginTop: 12 }}>
          <div className="pd__detail-grid">
            <div className="pd__detail-field"><span className="pd__detail-label">Kit ID</span><span className="pd__detail-value pd__cell-mono">{d.kitId || "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Patient</span><span className="pd__detail-value">{`${d.firstName || ""} ${d.lastName || ""}`.trim() || "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Email</span><span className="pd__detail-value">{d.email || "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Order ID</span><span className="pd__detail-value pd__cell-mono">{d.orderId || "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Order Created</span><span className="pd__detail-value">{formatDate(d.orderCreateDate, timezone)}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Order Status</span><span className="pd__detail-value">{d.orderStatus || "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Kit Received at Lab</span><span className="pd__detail-value">{formatDate(d.kitReceivedAtLabDate, timezone)}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Test Result Date</span><span className="pd__detail-value">{formatDate(d.testResultDate, timezone)}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">STI Completed</span><span className="pd__detail-value">{d.stiTestingCompleted ? "Yes" : "No"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Days to STI</span><span className="pd__detail-value">{d.daysToCompleteStiTesting != null ? fmtDays(d.daysToCompleteStiTesting) : "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">IHD Days to Ship</span><span className="pd__detail-value">{d.daysIhdToShip != null ? fmtDays(d.daysIhdToShip) : "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Shipped Within SLA</span><span className="pd__detail-value">{d.ihdShippedWithinSla == null ? "\u2014" : d.ihdShippedWithinSla ? "Yes" : "No"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Lab Days (Kit to Result)</span><span className="pd__detail-value">{d.daysResultsAfterTesting != null ? fmtDays(d.daysResultsAfterTesting) : "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">PrEP Rx</span><span className="pd__detail-value">{d.receivedPrepRx ? "Yes" : "No"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Beluga Visit</span><span className="pd__detail-value">{d.belugaVisitStatus || "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Maintenance</span><span className="pd__detail-value">{d.maintenancePatient ? "Yes" : "No"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Days Since Last Rx</span><span className="pd__detail-value">{d.daysSinceLastRx != null ? Math.round(d.daysSinceLastRx) : "\u2014"}</span></div>
            <div className="pd__detail-field"><span className="pd__detail-label">Lapsed</span><span className="pd__detail-value">{d.lapsed ? <span className="pd__badge pd__badge--danger">Yes</span> : "No"}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main PrepDashboard Component ───────────────────────────────────────────

const PrepDashboard = ({ resetView }) => {
  const { showSnackbar } = useContext(SnackbarContext);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [patientsError, setPatientsError] = useState(null);

  const [partnerFilter, setPartnerFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("MONTHLY");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(getInitialTimezone);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [funnelFilter, setFunnelFilter] = useState(null);
  const [tableSearch, setTableSearch] = useState("");

  const fetchSeqRef = useRef(0);
  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setTableSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 250);
  };

  const filteredPatients = useMemo(() => {
    let list = patients;
    if (funnelFilter) {
      list = list.filter((p) => !!p[funnelFilter]);
    }
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      list = list.filter((p) => {
        const name = `${p.firstName || ""} ${p.lastName || ""}`.toLowerCase();
        const email = (p.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    return list;
  }, [patients, funnelFilter, debouncedSearch]);

  const buildFilterParams = useCallback(() => {
    const params = {};
    // TODO: Re-enable when partner filter is restored
    // if (partnerFilter) params.partnerId = partnerFilter;
    if (periodFilter === "CUSTOM" && appliedCustomRange) {
      const startDt = DateTime.fromISO(appliedCustomRange.from, { zone: selectedTimezone }).startOf("day");
      params.fromDate = startDt.toUTC().toISO();
      const endDt = DateTime.fromISO(appliedCustomRange.to, { zone: selectedTimezone }).endOf("day");
      params.toDate = endDt.toUTC().toISO();
    } else if (periodFilter !== "CUSTOM") {
      params.period = periodFilter;
    }
    params.timezone = selectedTimezone;
    return params;
  }, [partnerFilter, periodFilter, appliedCustomRange, selectedTimezone]);

  const fetchSummary = useCallback(async (seq) => {
    setSummaryLoading(true);
    try {
      const res = await ApiService.getPrepDashboardSummary(buildFilterParams());
      if (seq !== fetchSeqRef.current) return;
      setSummary(res.data);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("Failed to load PrEP dashboard summary", err);
      showSnackbar({ message: "Failed to load dashboard summary.", severity: "error" });
    } finally {
      if (seq === fetchSeqRef.current) setSummaryLoading(false);
    }
  }, [buildFilterParams, showSnackbar]);

  const fetchPatients = useCallback(async (seq) => {
    setPatientsLoading(true);
    setPatientsError(null);
    try {
      const res = await ApiService.getPrepDashboardPatients(buildFilterParams());
      if (seq !== fetchSeqRef.current) return;
      setPatients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("Failed to load PrEP dashboard patients", err);
      setPatientsError("Failed to load patient data. Please try again.");
    } finally {
      if (seq === fetchSeqRef.current) setPatientsLoading(false);
    }
  }, [buildFilterParams]);

  const shouldFetch = periodFilter !== "CUSTOM" || !!appliedCustomRange;

  useEffect(() => {
    if (!shouldFetch) return;
    const seq = ++fetchSeqRef.current;
    setSelectedEmail(null);
    setFunnelFilter(null);
    setTableSearch("");
    setDebouncedSearch("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchSummary(seq);
    fetchPatients(seq);
  }, [fetchSummary, fetchPatients, shouldFetch]);

  useEffect(() => {
    if (periodFilter !== "CUSTOM") setAppliedCustomRange(null);
  }, [periodFilter]);

  const isCustom = periodFilter === "CUSTOM";
  const activeStep = funnelFilter ? ALL_FUNNEL_STEPS.find((s) => s.key === funnelFilter) : null;

  const handleFunnelClick = (step) => {
    if (!step.key) return;
    setFunnelFilter(funnelFilter === step.key ? null : step.key);
  };

  return (
    <div className="pd">
      <div className="pd__top">
        <button className="pd__back" onClick={resetView}>
          <BsArrowLeft /> Back to Admin Panel
        </button>
      </div>

      <h2 className="pd__title">PrEP Dashboard</h2>

      {/* ── Filter Controls ───────────────────────────── */}
      <div className="pd__filters">
        {/* TODO: Re-enable partner filter when backend aggregation is fixed
        <div className="pd__filter-group">
          <span className="pd__filter-label">Partner</span>
          <select className="pd__filter-select" value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}>
            {PARTNER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        */}

        <div className="pd__filter-group">
          <span className="pd__filter-label">Period</span>
          <div className="pd__filter-btns">
            {PERIOD_OPTIONS.map((o) => (
              <button
                key={o.value}
                className={`pd__filter-btn${periodFilter === o.value ? " pd__filter-btn--active" : ""}`}
                onClick={() => setPeriodFilter(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {isCustom && (
          <>
            <div className="pd__filter-group">
              <span className="pd__filter-label">From</span>
              <input type="date" className="pd__filter-input pd__filter-input--date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setAppliedCustomRange(null); }} />
            </div>
            <div className="pd__filter-group">
              <span className="pd__filter-label">To</span>
              <input type="date" className="pd__filter-input pd__filter-input--date" value={toDate} onChange={(e) => { setToDate(e.target.value); setAppliedCustomRange(null); }} />
            </div>
            {appliedCustomRange ? (
              <button
                className="pd__action-btn"
                onClick={() => { setAppliedCustomRange(null); setFromDate(""); setToDate(""); }}
              >
                Reset
              </button>
            ) : (
              <button
                className="pd__action-btn pd__action-btn--primary"
                disabled={!fromDate || !toDate}
                onClick={() => setAppliedCustomRange({ from: fromDate, to: toDate })}
              >
                Apply
              </button>
            )}
          </>
        )}

        <div className="pd__filter-group">
          <span className="pd__filter-label">Timezone</span>
          <select className="pd__filter-select" value={selectedTimezone} onChange={(e) => setSelectedTimezone(e.target.value)}>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Active Filter Banner ──────────────────────── */}
      <FilterBanner
        partner={partnerFilter}
        period={periodFilter}
        fromDate={fromDate}
        toDate={toDate}
        timezone={selectedTimezone}
        summary={!summaryLoading ? summary : null}
        onClearPartner={() => setPartnerFilter("")}
      />

      {/* ── Funnel + KPI Layout ───────────────────────── */}
      <div className="pd__funnel-layout">
        <div className="pd__funnel">
          <div className="pd__funnel-section">
            <div className="pd__funnel-section-label">Patient Journey</div>
            {INTAKE_FUNNEL.map((step) => (
              <FunnelStep
                key={step.countKey}
                step={step}
                data={summary}
                loading={summaryLoading}
                topCount={summary?.totalJotformSubmissions}
                isActive={funnelFilter === step.key}
                onClick={() => handleFunnelClick(step)}
              />
            ))}
          </div>
          <div className="pd__funnel-section">
            <div className="pd__funnel-section-label">Maintenance Cohort</div>
            {MAINTENANCE_FUNNEL.map((step) => (
              <FunnelStep
                key={step.countKey}
                step={step}
                data={summary}
                loading={summaryLoading}
                topCount={summary?.maintenancePatientCount}
                isActive={funnelFilter === step.key}
                onClick={() => handleFunnelClick(step)}
              />
            ))}
          </div>
        </div>

        <KpiPanel data={summary} loading={summaryLoading} />
      </div>

      {/* ── Active Funnel Chip ────────────────────────── */}
      {activeStep && (
        <div className="pd__chip-bar">
          <span className="pd__chip">
            Drill-down: {activeStep.label}
            <button className="pd__chip-clear" onClick={() => setFunnelFilter(null)}>&times;</button>
          </span>
        </div>
      )}

      {/* ── Patient Detail (if selected) ──────────────── */}
      {selectedEmail && (
        <PatientDetail
          email={selectedEmail}
          partnerId="" /* TODO: restore {partnerFilter} when partner filter is re-enabled */
          onClose={() => setSelectedEmail(null)}
          timezone={selectedTimezone}
        />
      )}

      {/* ── Patient Table ─────────────────────────────── */}
      <div className="pd__table-toolbar">
        <div className="pd__table-toolbar-left">
          <h3 className="pd__section-title">
            {activeStep ? activeStep.label : "All Patients"}
          </h3>
          {!patientsLoading && !patientsError && (
            <span className="pd__table-count">
              {filteredPatients.length === patients.length
                ? `${patients.length} patient${patients.length !== 1 ? "s" : ""}`
                : `${filteredPatients.length} of ${patients.length} patients`}
            </span>
          )}
        </div>
        <input
          type="text"
          className="pd__filter-input pd__filter-input--table-search"
          placeholder="Search name or email..."
          value={tableSearch}
          onChange={handleSearchChange}
        />
      </div>
      <div className="pd__table-wrap">
        {patientsLoading ? (
          <div className="pd__loading"><Spinner animation="border" /><div style={{ marginTop: 12 }}>Loading patients...</div></div>
        ) : patientsError ? (
          <div className="pd__error">{patientsError}<br /><button className="pd__action-btn" onClick={fetchPatients}>Retry</button></div>
        ) : filteredPatients.length === 0 ? (
          <div className="pd__empty">
            {patients.length === 0
              ? "No patient data for the selected filters."
              : "No patients match the current search or funnel filter."}
          </div>
        ) : (
          <table className="pd__table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Name</th>
                <th>Email</th>
                <th>Created</th>
                <th>Partner</th>
                <th>Validated</th>
                <th>STI Done</th>
                <th>Days to STI</th>
                <th>PrEP Rx</th>
                <th>Maintenance</th>
                <th>Days Since Rx</th>
                <th>Lapsed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((p, i) => {
                const stage = getPatientStage(p);
                return (
                  <tr key={`${p.orderId}-${i}`} className={selectedEmail === p.email ? "pd__row--selected" : ""}>
                    <td>
                      <span className={`pd__stage pd__badge--${stage.color}`}>{stage.label}</span>
                    </td>
                    <td className="pd__cell-name">{`${p.firstName || ""} ${p.lastName || ""}`.trim() || "\u2014"}</td>
                    <td>{p.email || "\u2014"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(p.orderCreateDate, selectedTimezone)}</td>
                    <td>{p.partnerId || "\u2014"}</td>
                    <td>{p.validatedPatient ? <span className="pd__badge pd__badge--success">Yes</span> : "No"}</td>
                    <td>{p.stiTestingCompleted ? <span className="pd__badge pd__badge--success">Yes</span> : "No"}</td>
                    <td>{p.daysToCompleteStiTesting != null ? fmtDays(p.daysToCompleteStiTesting) : "\u2014"}</td>
                    <td>{p.receivedPrepRx ? <span className="pd__badge pd__badge--success">Yes</span> : "No"}</td>
                    <td>{p.maintenancePatient ? "Yes" : "No"}</td>
                    <td>{p.daysSinceLastRx != null ? Math.round(p.daysSinceLastRx) : "\u2014"}</td>
                    <td>{p.lapsed ? <span className="pd__badge pd__badge--danger">Yes</span> : "No"}</td>
                    <td>
                      <button className="pd__action-btn" onClick={() => setSelectedEmail(p.email)}>View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Kit Lookup ────────────────────────────────── */}
      <KitLookup timezone={selectedTimezone} />
    </div>
  );
};

export default PrepDashboard;
